import type { DraftFrameRangeT } from '../../../../modernization-common/relationships';
import { CARDINALITY_DISPLAY, CARDINALITY_ERROR_CONTEXT_DISPLAY } from './constants';
import type { CardinalityErrorT } from './validation-utils';
type AnnotationFrameRange = {
  startFrameIndex: number;
  endFrameIndex: number;
};
type FrameRangeLimitsResult = {
  frameLimits: DraftFrameRangeT[];
  frameRanges: DraftFrameRangeT[];
};
export const computeTargetFrameRangeLimits = (sourceFrameRanges: AnnotationFrameRange[], targetFrameRanges: AnnotationFrameRange[]): FrameRangeLimitsResult => {
  // Non-temporal (image task): no limits, no initial ranges
  if (!sourceFrameRanges.length && !targetFrameRanges.length) {
    return {
      frameLimits: [],
      frameRanges: []
    };
  }

  // Source has ranges but no target — use source ranges as both limits and initial
  if (!targetFrameRanges.length) {
    const sourceRanges = sourceFrameRanges.map(({
      startFrameIndex,
      endFrameIndex
    }) => ({
      startFrame: startFrameIndex + 1,
      endFrame: endFrameIndex + 1
    }));
    return {
      frameLimits: sourceRanges,
      frameRanges: sourceRanges
    };
  }

  // Compute pairwise intersections of source and target ranges
  const overlaps: DraftFrameRangeT[] = [];
  for (const src of sourceFrameRanges) {
    for (const tgt of targetFrameRanges) {
      const start = Math.max(src.startFrameIndex, tgt.startFrameIndex) + 1;
      const end = Math.min(src.endFrameIndex, tgt.endFrameIndex) + 1;
      if (start <= end) {
        overlaps.push({
          startFrame: start,
          endFrame: end
        });
      }
    }
  }
  if (overlaps.length) {
    return {
      frameLimits: overlaps,
      frameRanges: overlaps
    };
  }

  // No overlap: return source ranges as limits (for validation error reporting)
  // and target ranges as initial frame ranges
  return {
    frameLimits: sourceFrameRanges.map(({
      startFrameIndex,
      endFrameIndex
    }) => ({
      startFrame: startFrameIndex + 1,
      endFrame: endFrameIndex + 1
    })),
    frameRanges: targetFrameRanges.map(({
      startFrameIndex,
      endFrameIndex
    }) => ({
      startFrame: startFrameIndex + 1,
      endFrame: endFrameIndex + 1
    }))
  };
};
export const formatCardinalityError = (err: CardinalityErrorT, showFramesInfo?: boolean): string => {
  const hasFrames = showFramesInfo && err.conflictFrameRanges.length > 0;
  const framesPart = hasFrames ? ` between frames ${err.conflictFrameRanges[0]!.startFrameIndex + 1}→${err.conflictFrameRanges[0]!.endFrameIndex + 1}` : '';
  const outsidePart = hasFrames ? ' outside these frames' : '';
  return `A ${CARDINALITY_DISPLAY[err.cardinalityValue]} relationship already exists for this ${CARDINALITY_ERROR_CONTEXT_DISPLAY[err.context]}${framesPart}. To proceed, modify the existing one or create a new one${outsidePart}.`;
};